import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../api/client';
import type { Automation, AutomationRun, BucketInfo, RetentionConfig } from '../api/types';
import { INTERVAL_PRESETS, RunHistory, RunResult, StatusPill, ToggleSwitch } from '../components/automation-ui';

const inputCls =
  'w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy';
const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';

/** Editable settings for one automation; seeded from the loaded row, saved via PATCH. */
function SettingsForm({ automation, buckets }: { automation: Automation; buckets: BucketInfo[] }) {
  const qc = useQueryClient();
  const cfg = automation.config as RetentionConfig;
  const [name, setName] = useState(automation.name);
  const [bucket, setBucket] = useState(cfg.bucket ?? '');
  const [pattern, setPattern] = useState(cfg.pattern ?? '');
  const [olderThanDays, setOlderThanDays] = useState(cfg.olderThanDays?.toString() ?? '');
  const [keepNewest, setKeepNewest] = useState(cfg.keepNewest?.toString() ?? '');
  const [maxDeletes, setMaxDeletes] = useState(cfg.maxDeletesPerRun?.toString() ?? '');
  const [intervalMinutes, setIntervalMinutes] = useState(automation.interval_minutes);

  const toOptional = (v: string) => (v.trim() ? Number(v) : undefined);

  const save = useMutation({
    mutationFn: () => {
      const config: RetentionConfig = {
        bucket,
        pattern: pattern.trim(),
        ...(toOptional(olderThanDays) !== undefined ? { olderThanDays: Number(olderThanDays) } : {}),
        ...(toOptional(keepNewest) !== undefined ? { keepNewest: Number(keepNewest) } : {}),
        ...(toOptional(maxDeletes) !== undefined ? { maxDeletesPerRun: Number(maxDeletes) } : {}),
      };
      return api<Automation>(`/api/admin/automations/${automation.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), intervalMinutes, config }),
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(['automation', automation.id], updated);
      void qc.invalidateQueries({ queryKey: ['automations'] });
    },
  });

  const dirty =
    name.trim() !== automation.name ||
    intervalMinutes !== automation.interval_minutes ||
    bucket !== (cfg.bucket ?? '') ||
    pattern.trim() !== (cfg.pattern ?? '') ||
    toOptional(olderThanDays) !== cfg.olderThanDays ||
    toOptional(keepNewest) !== cfg.keepNewest ||
    toOptional(maxDeletes) !== cfg.maxDeletesPerRun;

  const canSave = dirty && !!name.trim() && !!bucket && !!pattern.trim() && (!!olderThanDays.trim() || !!keepNewest.trim());

  // The hourly sweep can't honor sub-hour intervals, so the form only offers presets;
  // an existing non-preset value (set via API) is kept selectable rather than clobbered.
  const hasPreset = INTERVAL_PRESETS.some((p) => p.minutes === intervalMinutes);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-navy">Settings</h2>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Name</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <input className={`${inputCls} bg-gray-50 text-gray-500`} value={automation.type} disabled />
            <p className="mt-1 text-xs text-gray-500">The type is fixed after creation.</p>
          </div>
          <div>
            <label className={labelCls}>Runs</label>
            <select className={inputCls} value={intervalMinutes} onChange={(e) => setIntervalMinutes(Number(e.target.value))}>
              {!hasPreset ? <option value={intervalMinutes}>Every {intervalMinutes} min</option> : null}
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.minutes} value={p.minutes}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Bucket</label>
              <select className={inputCls} value={bucket} onChange={(e) => setBucket(e.target.value)}>
                {buckets.map((b) => (
                  <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Path pattern</label>
              <input
                className={`${inputCls} font-mono`}
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                placeholder="releases/odincli/**"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-gray-500">
                Patterns are explicit: <span className="font-mono">sql/**</span> = everything under{' '}
                <span className="font-mono">sql</span>, <span className="font-mono">sql/*</span> = its direct files only,{' '}
                <span className="font-mono">sql/**/*.bak</span> = .bak files at any depth. Must start with a literal
                folder — whole-bucket cleanup is not allowed. No trailing <span className="font-mono">/</span>.
              </p>
            </div>
          </div>

          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Delete older than (days)</label>
              <input type="number" min={1} className={inputCls} value={olderThanDays} onChange={(e) => setOlderThanDays(e.target.value)} placeholder="e.g. 30" />
            </div>
            <div>
              <label className={labelCls}>Always keep newest</label>
              <input type="number" min={1} className={inputCls} value={keepNewest} onChange={(e) => setKeepNewest(e.target.value)} placeholder="e.g. 10" />
            </div>
            <div>
              <label className={labelCls}>Max deletions per run</label>
              <input type="number" min={1} className={inputCls} value={maxDeletes} onChange={(e) => setMaxDeletes(e.target.value)} placeholder="500" />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            At least one of age / keep-newest is required. Keep-newest always wins: the newest N matching files are
            never deleted, even when older than the age cutoff.
          </p>
        </div>
      </div>

      {save.isError ? <p className="mt-3 text-sm text-red-600">{getErrorMessage(save.error)}</p> : null}

      <div className="mt-4 flex items-center gap-3">
        <button
          className="rounded bg-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!canSave || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {save.isSuccess && !dirty ? <span className="text-sm text-green-700">Saved.</span> : null}
        {dirty ? <span className="text-xs text-gray-500">Unsaved changes</span> : null}
      </div>
    </div>
  );
}

export function AutomationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [lastRun, setLastRun] = useState<AutomationRun | null>(null);

  const automation = useQuery({
    queryKey: ['automation', id],
    queryFn: () => api<Automation>(`/api/admin/automations/${id}`),
  });
  const buckets = useQuery({ queryKey: ['buckets'], queryFn: () => api<{ buckets: BucketInfo[] }>('/api/admin/buckets') });

  // Optimistic toggle: flip the cached row immediately, roll back if the PATCH fails.
  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      api<Automation>(`/api/admin/automations/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled: next }) }),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ['automation', id] });
      const prev = qc.getQueryData<Automation>(['automation', id]);
      if (prev) qc.setQueryData(['automation', id], { ...prev, enabled: next });
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(['automation', id], ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['automation', id] });
      void qc.invalidateQueries({ queryKey: ['automations'] });
    },
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<AutomationRun>(`/api/admin/automations/${id}/run`, { method: 'POST', body: JSON.stringify({ dryRun }) }),
    onSuccess: (result) => {
      setLastRun(result);
      void qc.invalidateQueries({ queryKey: ['automation', id] });
      void qc.invalidateQueries({ queryKey: ['automation-runs', id] });
      void qc.invalidateQueries({ queryKey: ['automations'] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api(`/api/admin/automations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] });
      void navigate('/automations');
    },
  });

  const a = automation.data;

  return (
    <div>
      <Link to="/automations" className="text-sm text-navy hover:underline">← Automations</Link>

      {automation.isLoading ? <div className="mt-4 text-sm">Loading…</div> : null}
      {automation.isError ? <div className="mt-4 text-sm text-red-600">{getErrorMessage(automation.error)}</div> : null}

      {a ? (
        <>
          <div className="mt-2 mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-navy">{a.name}</h1>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                <span>{a.type}</span>
                <StatusPill status={a.last_status} />
                {a.last_run_utc ? <span>last run {new Date(a.last_run_utc).toLocaleString()}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <ToggleSwitch checked={a.enabled} disabled={toggle.isPending} onChange={(next) => toggle.mutate(next)} />
                {a.enabled ? 'Enabled' : 'Disabled'}
              </label>
              <button
                className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                disabled={run.isPending}
                onClick={() => run.mutate(true)}
              >
                Preview
              </button>
              <button
                className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                disabled={run.isPending}
                onClick={() => run.mutate(false)}
              >
                Run now
              </button>
              <button
                className="rounded border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (confirm(`Delete automation '${a.name}'? Its run history is removed too.`)) remove.mutate();
                }}
              >
                Delete
              </button>
            </div>
          </div>

          {a.last_error ? <p className="mb-3 text-sm text-red-600">Last run: {a.last_error}</p> : null}
          {run.isError ? <p className="mb-3 text-sm text-red-600">{getErrorMessage(run.error)}</p> : null}
          {run.isPending ? <p className="mb-3 text-sm text-gray-500">Running…</p> : null}
          {lastRun ? (
            <div className="mb-4">
              <RunResult run={lastRun} />
            </div>
          ) : null}

          <SettingsForm key={a.id} automation={a} buckets={buckets.data?.buckets ?? []} />

          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-navy">Run history</h2>
            <RunHistory automationId={a.id} />
          </div>
        </>
      ) : null}
    </div>
  );
}
